/**
 * Emergency Contact Editor Component
 * Manages emergency contacts for each parent in the family.
 * Each parent has their own emergency_contacts[] array.
 */

import React, { useState, useEffect } from 'react';
import { Trash2, Plus } from 'lucide-react';
import Button from './Button';
import Badge from './Badge';
import { FormInput } from './FormInput';
import styles from './EmergencyContactEditor.module.css';

const EmergencyContactEditor = ({
  parents = [],
  existingContacts = [],
  onSave,
  onCancel,
  readOnly = false
}) => {
  // State for user-type contacts (parents)
  // Set of parent IDs that are selected as emergency contacts
  const [designatedParentIds, setDesignatedParentIds] = useState(new Set());

  // State for external-type contacts
  const [externalContacts, setExternalContacts] = useState([]);

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  
  // Track initial state for change detection
  const [initialDesignatedParentIds, setInitialDesignatedParentIds] = useState(new Set());
  const [initialExternalContacts, setInitialExternalContacts] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize state from existingContacts
  useEffect(() => {
    const initialDesignated = new Set();
    const initialExternal = [];

    if (existingContacts && Array.isArray(existingContacts)) {
      existingContacts.forEach(contact => {
        // Handle both camelCase and snake_case for user_id/userId if necessary, though backend usually uses user_id
        const userId = contact.user_id || contact.userId;

        if (contact.type === 'user' && userId) {
          initialDesignated.add(userId);
        } else if (contact.type === 'external') {
          initialExternal.push({ ...contact });
        }
      });
    }

    setDesignatedParentIds(initialDesignated);
    setExternalContacts(initialExternal);
    setInitialDesignatedParentIds(initialDesignated);
    setInitialExternalContacts(initialExternal);
  }, [existingContacts]);
  
  // Detect changes
  useEffect(() => {
    // Check if parent designations have changed
    const parentIdsChanged = 
      designatedParentIds.size !== initialDesignatedParentIds.size ||
      Array.from(designatedParentIds).some(id => !initialDesignatedParentIds.has(id)) ||
      Array.from(initialDesignatedParentIds).some(id => !designatedParentIds.has(id));
    
    // Check if external contacts have changed
    const externalContactsChanged = 
      externalContacts.length !== initialExternalContacts.length ||
      externalContacts.some((contact, idx) => {
        const initial = initialExternalContacts[idx];
        return !initial || 
          contact.name !== initial.name || 
          contact.phone !== initial.phone || 
          contact.relationship !== initial.relationship;
      });
    
    setHasChanges(parentIdsChanged || externalContactsChanged);
  }, [designatedParentIds, externalContacts, initialDesignatedParentIds, initialExternalContacts]);

  const handleToggleDesignation = (parentId, isChecked) => {
    setDesignatedParentIds(prev => {
      const next = new Set(prev);
      if (isChecked) {
        next.add(parentId);
      } else {
        next.delete(parentId);
      }
      return next;
    });
  };

  // External Contact Management
  const handleAddExternalContact = () => {
    setExternalContacts(prev => [
      ...prev,
      { type: 'external', name: '', phone: '', relationship: '' }
    ]);
  };

  const handleExternalContactChange = (index, field, value) => {
    setExternalContacts(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleRemoveExternalContact = (index) => {
    setExternalContacts(prev => prev.filter((_, i) => i !== index));
  };

  // Validate and save
  const handleSave = async () => {
    const newErrors = {};

    // Validate designated parents
    // (Nothing specific to validate for checkboxes other than logic requirements if any)

    // Validate external contacts
    externalContacts.forEach((contact, idx) => {
      if (!contact.name) newErrors[`external-${idx}-name`] = 'Name is required';
      if (!contact.phone) newErrors[`external-${idx}-phone`] = 'Phone is required';
      if (!contact.relationship) newErrors[`external-${idx}-relationship`] = 'Relationship is required';
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setMessage({ type: 'error', text: 'Please fix validation errors before saving' });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      // 1. Construct User Type Contacts
      const userContacts = Array.from(designatedParentIds).map(userId => ({
        type: 'user',
        user_id: userId,
        relationship: 'Primary Contact' // Or could be derived from parent data if needed
      }));

      // 2. Construct External Type Contacts
      const finalExternalContacts = externalContacts.map(c => ({
        type: 'external',
        name: c.name,
        phone: c.phone,
        relationship: c.relationship
      }));

      // 3. Combine
      const consolidatedList = [...userContacts, ...finalExternalContacts];

      if (onSave) {
        await onSave(consolidatedList);
      }

      // Success handling is now done by parent component toast
      setErrors({});
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.container}>
      {message && (
        <div className={styles[`message-${message.type}`]}>
          {message.text}
        </div>
      )}

      {/* Section 1: Parents (User Contacts) */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Family Contacts</h3>
        <p className={styles.sectionSubtitle}>Select parents to list as emergency contacts</p>

        {parents.length === 0 ? (
          <div className={styles.noParents}>No parents found.</div>
        ) : (
          <div className={styles.parentsContainer}>
            {parents.map(parent => (
              <div key={parent.id} className={styles.parentCard}>
                <div className={styles.parentHeader}>
                  <h4 className={styles.parentName}>
                    {parent.first_name} {parent.last_name}
                  </h4>
                  <Badge variant="success" size="sm">
                    {parent.family_role === 'primary_parent' ? 'Primary Parent' : 'Secondary Parent'}
                  </Badge>
                </div>

                {!readOnly && (
                  <div className={styles.designationCheckbox}>
                    <label>
                      <input
                        type="checkbox"
                        checked={designatedParentIds.has(parent.id)}
                        onChange={e => handleToggleDesignation(parent.id, e.target.checked)}
                      />
                      <span>Designate as emergency contact</span>
                    </label>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Section 2: Additional (External Contacts) */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div>
            <h3 className={styles.sectionTitle}>Additional Emergency Contacts</h3>
            <p className={styles.sectionSubtitle}>Add other contacts (grandparents, neighbors, etc.)</p>
          </div>
          {!readOnly && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddExternalContact}
            >
              <Plus size={16} /> Add Contact
            </Button>
          )}
        </div>

        <div className={styles.externalContactsList}>
          {externalContacts.map((contact, index) => (
            <div key={index} className={styles.externalContactRow}>
              <div className={styles.formGrid}>
                <FormInput
                  label="Name"
                  value={contact.name}
                  onChange={e => handleExternalContactChange(index, 'name', e.target.value)}
                  error={errors[`external-${index}-name`]}
                  disabled={readOnly}
                />
                <FormInput
                  label="Phone"
                  value={contact.phone}
                  onChange={e => handleExternalContactChange(index, 'phone', e.target.value)}
                  error={errors[`external-${index}-phone`]}
                  disabled={readOnly}
                />
                <FormInput
                  label="Relationship"
                  value={contact.relationship}
                  onChange={e => handleExternalContactChange(index, 'relationship', e.target.value)}
                  error={errors[`external-${index}-relationship`]}
                  disabled={readOnly}
                />
              </div>
              {!readOnly && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleRemoveExternalContact(index)}
                  title="Remove contact"
                  className={styles.deleteButton}
                >
                  <Trash2 size={16} />
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Action buttons */}
      {!readOnly && (
        <div className={styles.actionButtons}>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            {saving ? 'Saving...' : 'Save Emergency Contacts'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default EmergencyContactEditor;
